import React, { useState } from 'react';
import { School, Plus, Edit, Trash2 } from 'lucide-react';
import type { University } from '../../types';
import SearchInput from '../ui/SearchInput';
import EmptyState from '../ui/EmptyState';
import ConfirmDialog from '../ui/ConfirmDialog';

interface UniversityTableProps {
  universities: University[];
  onAdd: () => void;
  onEdit: (university: University) => void;
  onDelete: (id: string) => void;
}

const UniversityTable: React.FC<UniversityTableProps> = ({ universities, onAdd, onEdit, onDelete }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<University | null>(null);

  const filtered = universities.filter(uni => {
    const q = searchQuery.toLowerCase();
    return uni.name.toLowerCase().includes(q)
      || uni.code.toLowerCase().includes(q)
      || uni.domain.toLowerCase().includes(q);
  });

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <School className="w-5 h-5 mr-2" />
            Universities
            <span className="ml-2 text-sm font-normal text-gray-500">({universities.length})</span>
          </h2>
          <button
            onClick={onAdd}
            className="bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 transition-colors flex items-center text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </button>
        </div>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name, code, or domain..."
        />
      </div>
      <div className="overflow-x-auto">
        {filtered.length === 0 ? (
          <EmptyState
            icon={<School className="w-8 h-8" />}
            title={searchQuery ? 'No universities match your search' : 'No universities yet'}
            description={searchQuery ? 'Try a different search term' : 'Add your first university to get started'}
          />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(university => (
                <tr key={university.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{university.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded bg-indigo-100 text-indigo-800">
                      {university.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{university.domain}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => onEdit(university)}
                      className="text-indigo-600 hover:text-indigo-900 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(university)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete University"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This will also remove all associated users.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => {
            onDelete(confirmDelete.id);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default UniversityTable;
